import * as mobx from 'mobx';
import { IAppsyncSubscription } from '../../../../../../infrastructure/utils/appsync.utils';
import { PagingViewModel } from '../../../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import type { IAttendeesLiveFormContainerProps } from '../attendees-live-form.container';
import { IAttendeeDetailsDTO } from '../../../../../../api/attendees.dao';
import type { IRegistrant } from './partisipants-live-form-registrants-tab.viewmodel';
import { DEFAULT_PAGE_SIZE } from '../../../../../../constants/pagination.contants';

abstract class ParticipantsLiveFormTabBaseViewModel<T> {
    protected accountId: number;
    protected eventId: number;
    protected meetingId: number;
    protected subscription: IAppsyncSubscription;
    protected pagingViewModel: PagingViewModel;

    @mobx.observable
    protected items: T[];
    @mobx.observable
    protected totalNumber: number;
    @mobx.observable
    protected isLoading: boolean;
    @mobx.observable
    protected loadedPagesCount: number;
    @mobx.observable
    protected numberOfElementsOnLastPage: number;
    @mobx.observable
    public fetchingParticipants: boolean;
    @mobx.observable
    public loadMorePaginationToken: string;
    @mobx.observable
    public shouldReFetchAttendees: boolean;

    constructor() {
        this.pagingViewModel = new PagingViewModel({
            pageSizeOptions: [DEFAULT_PAGE_SIZE],
            pageSize: DEFAULT_PAGE_SIZE
        });
        this.loadedPagesCount = 1;
        this.fetchingParticipants = false;
        this.items = [];
        this.loadMorePaginationToken = '';
        this.shouldReFetchAttendees = false;
    };

    public abstract subscribe(): Promise<void>;
    protected abstract fetchParticipants(): Promise<void>;
    public abstract loadMoreParticipants(): Promise<void>;

    public get attendees(): T[] { return this.items; }
    public get total(): number { return this.totalNumber; }
    public get pagingVM(): PagingViewModel { return this.pagingViewModel; }

    public get hasMoreParticipants(): boolean {
        return Boolean(
            this.items &&
            this.totalNumber != null &&
            this.items.length < this.totalNumber
        );
    }

    public get shouldShowLoadMoreButton(): boolean {
        return Boolean(
            this.totalNumber > DEFAULT_PAGE_SIZE &&
            this.hasMoreParticipants
        );
    }

    public unsubscribe = (): void => {
        if (this.subscription) {
            this.subscription?.unsubscribe();
        }
    };

    @mobx.action
    public updateMeetingLiveData = (total: number) => {
        this.totalNumber = total;
    };

    @mobx.action
    public setActiveTab = (): void => {
        this.pagingVM.selectPage(1);
    };

    @mobx.action
    public initialize(formProps: IAttendeesLiveFormContainerProps): void {
        const {
            accountId,
            eventId,
            meetingId,
        } = formProps;

        this.fillInitialData(accountId, eventId, meetingId);

        this.fetchParticipants();
        this.subscribe();
    }

    @mobx.action
    public onAttendeeEditSaveCallback = (attendee: IAttendeeDetailsDTO): void => {
        const currentAttendeeIndex = (this.attendees as IRegistrant[]).findIndex(a => a.attendeeId === attendee.attendeeId);
        if (currentAttendeeIndex !== -1) {
            const currentAttendee = this.attendees[currentAttendeeIndex] as IRegistrant;
            (this.attendees[currentAttendeeIndex] as IRegistrant) = {
                ...currentAttendee,
                organizationName: attendee.organizationName,
                firstName: attendee.firstName,
                lastName: attendee.lastName,
            };
        }
    };

    protected fillInitialData(accountId: number, eventId: number, meetingId: number): void {
        this.accountId = accountId;
        this.eventId = eventId;
        this.meetingId = meetingId;
    }
}

export { ParticipantsLiveFormTabBaseViewModel };